import { Box } from '@mui/material';
import {
  DataGridPremium,
  GridRowId,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { AddButton, CustomColumnMenu, NoTableRowsOverlay } from '@retail/components';
import { AssortmentItemMinimal, CentralAssortmentItem } from '@retail/my-assortment/types';
import { dataGridHorizontalPaddingSmallClass } from '@shared/styles';
import { useAppTFunction } from '@retail/app/i18n';
import { GridCallbackDetails } from '@mui/x-data-grid/models/api';
import { ExternalItemSource } from '@retail/products/components';
import { useGroupedRowClassName } from '@retail/hooks';
import { useCentralAssortmentColumns } from './useCentralAssortmentColumns';
import { SupplierItemAssortmentTag } from '@retail/products/types';
import { useFilters } from '@retail/products/hooks';
import { centralAssortmentsFilters, CentralAssortmentsFilters } from '@retail/my-assortment/utils';

interface Props {
  items: CentralAssortmentItem[];
  itemsChecked: number[];
  loading: boolean;
  externalItemSource: ExternalItemSource;
  checkboxSelection?: boolean;
  setItemsChecked: (items: number[]) => void;
  viewItem: (itemId: number) => void;
  addItem?: (items: AssortmentItemMinimal) => void;
  editItem?: (item: CentralAssortmentItem) => void;
  editTags?: (item: CentralAssortmentItem) => void;
  deleteItem?: (itemId: number) => void;
  replaceItem?: (item: CentralAssortmentItem) => void;
  deleteAssortmentItemTag?: (tag: SupplierItemAssortmentTag, mgSupplierItemId: number) => void;
  toggleAddItemsDialog?: () => void;
  hideSubAssortments?: boolean;
  showMainSupplierName?: boolean;
}

const itemIdSelector = (assortmentItem: CentralAssortmentItem) => assortmentItem.mgSupplierItemId;

export function CentralAssortmentTable({
  items,
  itemsChecked,
  loading,
  externalItemSource,
  checkboxSelection,
  setItemsChecked,
  viewItem,
  addItem,
  editItem,
  editTags,
  deleteItem,
  replaceItem,
  deleteAssortmentItemTag,
  toggleAddItemsDialog,
  hideSubAssortments,
  showMainSupplierName,
}: Props) {
  const t = useAppTFunction();
  const apiRef = useGridApiRef();
  const getRowClassName = useGroupedRowClassName<CentralAssortmentItem>({
    apiRef,
    getGroupId: (row) => row.mgItemNumber,
  });
  const { setFilters, updateFilter } =
    useFilters<CentralAssortmentsFilters>(centralAssortmentsFilters);

  const selectionModel: GridRowSelectionModel = {
    type: 'include',
    ids: new Set<GridRowId>(itemsChecked),
  };

  const onRowsSelectionHandler = (
    selectionModel: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => {
    if (details.reason === 'singleRowSelection') {
      setItemsChecked(Array.from(selectionModel.ids) as number[]);
    } else if (itemsChecked.length !== items.length) {
      setItemsChecked(items.map(itemIdSelector));
    } else {
      setItemsChecked([]);
    }
  };

  const columns = useCentralAssortmentColumns({
    selectSupplier: (supplierNumber: number) =>
      setFilters({ supplierNumber: supplierNumber.toString() }),
    selectProductGroup: (groupNumber: string) => updateFilter('supplierNumber', groupNumber),
    selectRowId: viewItem,
    addItem,
    editItem,
    editTags,
    deleteItem,
    replaceItem,
    deleteAssortmentItemTag,
    externalItemSource,
  });

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium<CentralAssortmentItem>
          apiRef={apiRef}
          columns={columns}
          rows={items}
          getRowId={itemIdSelector}
          className={dataGridHorizontalPaddingSmallClass}
          getRowClassName={getRowClassName}
          getRowHeight={() => 'auto'}
          loading={loading}
          checkboxSelection={checkboxSelection}
          disableRowSelectionOnClick
          hideFooter
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={onRowsSelectionHandler}
          initialState={{ pinnedColumns: { right: ['actions'] } }}
          sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
          columnVisibilityModel={{
            addedBy: !!toggleAddItemsDialog,
            tagName: !hideSubAssortments,
            tagValidFrom: !hideSubAssortments,
            tagValidTo: !hideSubAssortments,
            mainSupplierName: !!showMainSupplierName,
          }}
          slots={{
            columnMenu: CustomColumnMenu,
            noRowsOverlay: () => (
              <NoTableRowsOverlay
                noRowsText={t('products.mgItem.noItems')}
                addAction={
                  !!toggleAddItemsDialog && (
                    <AddButton onClick={toggleAddItemsDialog}>{t('products.mgItem.add')}</AddButton>
                  )
                }
              />
            ),
          }}
        />
      </Box>
    </Box>
  );
}
